import { Link as RouterLink } from 'react-router-dom';
import { TableCellContainer } from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import { TestsLatestStatusCell } from '@/features/shared/components/tableSystem/TestsLatestStatusCell';
import { resolveEntityTags } from '@/features/shared/tags';
import { resolveTestEntityDisplayName } from '@/features/shared/utils/testDisplayName';
import { Inline, Link, Text, Tooltip } from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { TableNameCell, TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, type GridColDef, type GridRenderCellParams } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { TestRunStatus, TestListItemDto } from '@flowqa/shared';

type BuildCoreColumnsOptions = {
  selectedProjectId?: string | null;
  preferFileNameOverDisplayName?: boolean;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: TestRunStatus | null) => void;
  showSuiteLinks: boolean;
  combineNameAndSuite: boolean;
  includeSuiteInNameColumn: boolean;
  includeTagsInNameColumn: boolean;
  latestAsMeta: boolean;
  includeLastExecutedColumn: boolean;
  disableLinks: boolean;
  tagColorByName?: Record<string, TagColor>;
  columnFlex: {
    name: number;
    suite: number;
    latest: number;
    lastExecuted: number;
  };
};

const NUMERIC_TEXT_SX = {
  fontSize: '13px',
  lineHeight: 1.2,
};

export const buildCoreColumns = ({
  selectedProjectId = null,
  preferFileNameOverDisplayName = false,
  onProjectSelect,
  onStatusSelect,
  showSuiteLinks,
  combineNameAndSuite,
  includeSuiteInNameColumn,
  includeTagsInNameColumn,
  latestAsMeta,
  includeLastExecutedColumn,
  disableLinks,
  tagColorByName = {},
  columnFlex,
}: BuildCoreColumnsOptions): {
  nameColumn: GridColDef<TestListItemDto>;
  suiteColumn: GridColDef<TestListItemDto>;
  latestSummaryColumn: GridColDef<TestListItemDto>;
  lastExecutedColumn: GridColDef<TestListItemDto, Date | string | null> | null;
} => {
  const stringOperators = getStringFilterOperators();

  const nameColumn: GridColDef<TestListItemDto> = {
    field: 'name',
    headerName: 'Test',
    flex: columnFlex.name,
    align: 'left',
    headerAlign: 'left',
    cellClassName: combineNameAndSuite ? 'table-name-cell' : undefined,
    filterOperators: stringOperators,
    renderCell: (params) => {
      if (combineNameAndSuite) {
        const primarySuite = params.row.suites?.primary ?? params.row.suites?.items?.[0] ?? null;
        const testHref = disableLinks ? null : `/tests/${params.row.slug}`;
        const suiteHref =
          !disableLinks && showSuiteLinks && primarySuite ? `/suites/${primarySuite.slug}` : null;
        const suiteLabel = primarySuite
          ? preferFileNameOverDisplayName
            ? primarySuite.name
            : (primarySuite.displayName ?? primarySuite.name)
          : null;
        const tags = resolveEntityTags(params.row);
        const resolvedTags = tags.map((tag) => {
          const trimmed = tag.trim();
          const fallback = trimmed.toLowerCase();
          if (Object.prototype.hasOwnProperty.call(tagColorByName, trimmed)) {
            return { label: tag, tone: tagColorByName[trimmed] };
          }
          if (Object.prototype.hasOwnProperty.call(tagColorByName, fallback)) {
            return { label: tag, tone: tagColorByName[fallback] };
          }
          return { label: tag, tone: 'neutral' as const };
        });
        return (
          <TableCellContainer gap="none">
            <TableNameCell
              primary={{
                label: resolveTestEntityDisplayName(params.row, {
                  preferFileNameOverDisplayName,
                }),
                ...(testHref
                  ? {
                      href: testHref,
                      onClick: () => {
                        onProjectSelect(params.row.projectId);
                      },
                    }
                  : {}),
              }}
              secondary={
                includeSuiteInNameColumn && suiteLabel
                  ? {
                      label: suiteLabel,
                      role: 'caption',
                      ...(suiteHref ? { href: suiteHref } : {}),
                    }
                  : null
              }
              tags={includeTagsInNameColumn ? resolvedTags : []}
            />
          </TableCellContainer>
        );
      }

      return (
        <TableTextCell
          primary={{
            label: resolveTestEntityDisplayName(params.row, {
              preferFileNameOverDisplayName,
            }),
            ...(disableLinks
              ? {}
              : {
                  href: `/tests/${params.row.slug}`,
                  onClick: () => {
                    onProjectSelect(params.row.projectId);
                  },
                }),
          }}
        />
      );
    },
  };

  const suiteColumn: GridColDef<TestListItemDto> = {
    field: 'suiteName',
    headerName: 'Suite',
    flex: columnFlex.suite,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TestListItemDto>) => {
      if (!showSuiteLinks) {
        return <TagsInlineCell tags={[]} />;
      }
      const suiteItems = params.row.suites?.items ?? [];
      const suiteIds = params.row.suites?.ids ?? [];
      const tags = suiteItems.map((suite) =>
        preferFileNameOverDisplayName ? suite.name : (suite.displayName ?? suite.name),
      );
      const suiteLinks: Array<{ label: string; href: string }> = [];
      suiteItems.forEach((suite) => {
        const href = `/suites/${suite.slug}`;
        if (suiteLinks.some((item) => item.href === href)) return;
        const label = preferFileNameOverDisplayName
          ? suite.name
          : (suite.displayName ?? suite.name);
        suiteLinks.push({
          label,
          href,
        });
      });
      if (suiteLinks.length > 0) {
        return (
          <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <Inline
              gap="xs"
              wrap="nowrap"
              align="center"
              sx={{ width: '100%', minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {suiteLinks.map((suite, index) =>
                disableLinks ? (
                  <Text
                    key={`${suite.href}-${String(index)}`}
                    role="body"
                    truncate
                    component="span"
                    sx={[
                      {
                        display: 'inline-block',
                        minWidth: 0,
                        maxWidth: '100%',
                      },
                    ]}
                  >
                    {index < suiteLinks.length - 1 ? `${suite.label},` : suite.label}
                  </Text>
                ) : (
                  <Text
                    key={`${suite.href}-${String(index)}`}
                    role="body"
                    component="span"
                    sx={{ display: 'inline-block', minWidth: 0, maxWidth: '100%' }}
                  >
                    <Link
                      component={RouterLink}
                      to={suite.href}
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={[
                        {
                          display: 'inline-block',
                          minWidth: 0,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flexShrink: 1,
                        },
                      ]}
                    >
                      {index < suiteLinks.length - 1 ? `${suite.label},` : suite.label}
                    </Link>
                  </Text>
                ),
              )}
            </Inline>
          </Box>
        );
      }
      const fallbackTags = tags.length > 0 ? tags : suiteIds;
      return <TagsInlineCell tags={fallbackTags} />;
    },
  };

  const latestSummaryColumn: GridColDef<TestListItemDto> = {
    field: 'latestRunStatus',
    headerName: 'Latest Status',
    flex: tableColumnWidths.tests.latestStatus.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'table-latest-meta-cell' : undefined,
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TestListItemDto>) => (
      <TestsLatestStatusCell
        disableLinks={disableLinks}
        latestAsMeta={latestAsMeta}
        onProjectSelect={onProjectSelect}
        onStatusSelect={onStatusSelect}
        row={params.row}
        selectedProjectId={selectedProjectId}
      />
    ),
  };

  const lastExecutedColumn: GridColDef<TestListItemDto, Date | string | null> | null =
    includeLastExecutedColumn
      ? {
          field: 'latestRunTimestamp',
          headerName: 'Last Executed',
          flex: columnFlex.lastExecuted,
          align: 'right',
          headerAlign: 'right',
          filterable: false,
          valueGetter: (_value, row) => row.latestRun?.timestamp ?? null,
          renderCell: (params: GridRenderCellParams<TestListItemDto, Date | string | null>) => {
            const timestamp = params.value ?? null;
            const label = timestamp ? formatRelativeTime(timestamp) : '-';
            const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
            return (
              <Tooltip
                title={fullTimestamp}
                disableHoverListener={!fullTimestamp}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '15px',
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <span>
                  <Text role="body" component="span" sx={NUMERIC_TEXT_SX}>
                    {label.length > 0 ? label : '-'}
                  </Text>
                </span>
              </Tooltip>
            );
          },
        }
      : null;

  return { nameColumn, suiteColumn, latestSummaryColumn, lastExecutedColumn };
};
